<template>
  <transition name="p-message" mode="out-in">
    <div
      v-if="panelVisible"
      class="notification-panel p-shadow-4 p-m-2 p-p-2"
      role="alert"
    >
      <div class="notification-header p-d-flex p-jc-between p-ai-center p-mb-1">
        <div class="p-d-flex p-ai-center">
          <i
            class="pi pi-clock p-mr-2"
            style="font-size: 1.1rem; color: #f59e0b"
          ></i>
          <span class="p-text-bold" style="color: #4b5563; font-size: 1rem"
            >Técnicos sem marcações</span
          >
        </div>
        <div class="notification-actions">
          <Button
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-secondary p-button-sm compact-btn"
            style="width: 1.5rem; height: 1.5rem"
            @click="markRead"
            v-tooltip.left="'Fechar e marcar como lido'"
          />
        </div>
      </div>

      <div class="notification-body">
        <Loading :active="isFetching && !freeTimes.data.length" :isFullPage="false" />
        <div class="p-grid p-ai-center p-nogutter">
        <div
          v-for="time in freeTimes.data"
          :key="`${time.user}-${time.office}`"
          class="p-col-12 p-md-6 p-lg-4 p-xl-3 p-p-1"
        >
          <div
            class="tech-slot-card compact"
            :style="{ borderLeft: `4px solid ${getOfficeColor(time.office)}` }"
          >
            <div class="tech-header p-d-flex p-jc-between p-ai-center">
              <div class="tech-name">
                <i class="pi pi-user p-mr-1" style="font-size: 0.8rem"></i>
                <span class="p-text-truncate">{{ time.user }}</span>
              </div>
              <div
                class="compact-badge-custom"
                v-if="time.office"
                :style="getOfficeStyle(time.office)"
              >
                <i
                  class="pi pi-map-marker p-mr-1"
                  style="font-size: 0.6rem"
                ></i>
                {{ time.office }}
              </div>
            </div>
            <div class="time-chips">
              <span
                v-for="slot in time.freeSlots"
                :key="`${time.user}-${slot.start}-${slot.end}`"
                class="time-chip compact-chip"
              >
                {{ slot.start }} - {{ slot.end }}
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>

      <div class="p-d-flex p-jc-end p-mt-1">
        <Button
          label="Marcar como lido"
          icon="pi pi-check"
          class="p-button-sm p-button-text p-button-secondary p-mr-2 compact-action-btn"
          @click="markRead"
        />
        <Button
          label="Ir para a agenda"
          icon="pi pi-calendar"
          class="p-button-sm p-button-text p-button-primary compact-action-btn"
          @click="goToSupportMap($event)"
          @auxclick="goToSupportMap($event)"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import notificationsService from "@/services/notifications.service";
import { openInternalFromEvent } from "@/helpers/tabLinks";

const OFFICE_STYLES = {
  Faro: { backgroundColor: "#f59e0b", color: "#fff" },
  Lisboa: { backgroundColor: "#3b82f6", color: "#fff" },
  Porto: { backgroundColor: "#10b981", color: "#fff" },
};

const OFFICE_COLORS = {
  Faro: "#f59e0b",
  Lisboa: "#3b82f6",
  Porto: "#10b981",
};

export default {
  name: "NotificationsBell",
  props: {
    forceOpen: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    visible: false,
    freeTimes: {
      id: null,
      data: [],
    },
    timer: null,
    isFetching: false,
    pollIntervalMs: 5 * 60 * 1000,
    lastPayloadSignature: "",
  }),
  computed: {
    panelVisible() {
      return this.visible || this.forceOpen;
    },
  },
  watch: {
    forceOpen(val) {
      if (val) {
        this.getFreeTimes(true);
      }
    },
    "$store.state.auth.status.loggedIn"(val) {
      if (val) {
        this.getFreeTimes();
      }
    },
  },
  created() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.scheduleNextFetch(5000);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.clearScheduledFetch();
  },
  methods: {
    clearScheduledFetch() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    scheduleNextFetch(delay = this.pollIntervalMs) {
      this.clearScheduledFetch();
      this.timer = setTimeout(() => {
        this.getFreeTimes();
      }, delay);
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.clearScheduledFetch();
        return;
      }

      this.scheduleNextFetch(1000);
    },
    getPayloadSignature(data) {
      return JSON.stringify(
        data.map((entry) => ({
          user: entry.user,
          office: entry.office,
          freeSlots: (entry.freeSlots || []).map(
            (slot) => `${slot.start}-${slot.end}`
          ),
        }))
      );
    },
    async getFreeTimes(force = false) {
      if (this.isFetching) {
        this.scheduleNextFetch();
        return;
      }

      if (
        !this.$store.state.auth.status.loggedIn ||
        !this.can("seeFreeTimesSupport")
      ) {
        this.visible = false;
        this.freeTimes = { id: null, data: [] };
        this.lastPayloadSignature = "";
        this.scheduleNextFetch(10 * 60 * 1000);
        return;
      }

      this.isFetching = true;
      try {
        const response = await notificationsService.getFreeTimes(force);
        if (response && response.notModified) {
          if (!document.hidden) {
            this.scheduleNextFetch();
          }
          return;
        }
        const responseData =
          response && Array.isArray(response.data) ? response.data : [];
        const sortedData = responseData
          .slice()
          .sort((a, b) => (a.office || "").localeCompare(b.office || ""));
        const signature = this.getPayloadSignature(sortedData);

        if (
          signature !== this.lastPayloadSignature ||
          this.freeTimes.id !== (response?.id || null)
        ) {
          this.freeTimes = {
            id: response?.id || null,
            data: sortedData,
          };
          this.lastPayloadSignature = signature;
        }

        this.visible = sortedData.length > 0;
      } catch (error) {
        console.error("Error fetching free times:", error);
      } finally {
        this.isFetching = false;
      }

      if (!document.hidden) {
        this.scheduleNextFetch();
      }
    },
    markRead() {
      this.visible = false;
      this.$emit("close");
      if (this.freeTimes && this.freeTimes.id) {
        return notificationsService
          .freeTimesRead(this.freeTimes.id)
          .catch((err) => console.error(err));
      }
    },
    async goToSupportMap(event) {
      if (event.type === "auxclick" && event.button !== 1) return;
      if (event.type === "auxclick") event.preventDefault();
      await this.markRead();
      openInternalFromEvent(this, { path: `/support` }, event);
    },
    getOfficeStyle(office) {
      if (!office) return {};
      return (
        OFFICE_STYLES[office] || { backgroundColor: "#6b7280", color: "#fff" }
      );
    },
    getOfficeColor(office) {
      if (!office) return "#e5e7eb";
      return OFFICE_COLORS[office] || "#6b7280";
    },
  },
};
</script>

<style scoped>
.notification-panel {
  background-color: #fffbeb; /* Amber-50 equivalent */
  border-left: 6px solid #f59e0b;
  border-radius: 4px;
  margin-top: 1rem !important;
  margin-bottom: 1.25rem !important;
}

.notification-body {
  position: relative;
  min-height: 60px;
}

.tech-slot-card {
  background: white;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tech-slot-card.compact {
  padding: 0.25rem 0.5rem;
  min-height: auto;
}

.tech-name {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
}

.tech-slot-card.compact .tech-name {
  font-size: 0.8rem;
  margin-bottom: 0px;
}

.compact-badge-custom {
  font-size: 0.65rem;
  height: 1.1rem;
  line-height: normal;
  padding: 0 0.4rem;
  border-radius: 4px;
  display: flex !important;
  align-items: center;
  font-weight: 700;
  min-width: 1.5rem;
  justify-content: center;
}

.time-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.time-chip {
  background-color: #e0f2fe;
  color: #0369a1;
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  white-space: nowrap;
}

.time-chip.compact-chip {
  font-size: 0.8rem;
  padding: 1px 4px;
  font-weight: 600;
}

.compact-btn .p-button-icon {
  font-size: 0.8rem;
}

.compact-action-btn .p-button-label {
  font-size: 0.85rem;
}

/* Transition styles */
.p-message-enter-active,
.p-message-leave-active {
  transition: all 0.3s ease;
}
.p-message-enter,
.p-message-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
